import type {
  DeadlineAiPreviewDto,
  DeadlineAiQuotaDto,
  DeadlineArchiveReason,
  DeadlineSummaryDto,
} from '@prospero/shared';
import { DEADLINE_AI_PREVIEW_TIMEOUT_MS } from '@prospero/shared';
import type {
  ConsultantBulkCreateDeadlinesDto,
  ConsultantClaimDeadlinesDto,
  ConsultantUpdateDeadlineDto,
} from '@shared/dto/deadline/consultant-deadline-write.dto';
import type {
  ConsultantBulkCreateReport,
  ConsultantClaimableResponse,
  ConsultantClaimReport,
} from '@shared/dto/deadline/consultant-deadline-write.response';
import { inject } from 'inversify';
import { Injectable } from '../../decorators/injectable';
import { HttpService, type IHttpService } from '../http/http.service';
import type {
  ConsultantTakeOverInput,
  IConsultantDeadlineActionsService,
  RenewConsultantDeadlineInput,
} from './consultant-deadline-actions.service.interface';

@Injectable()
class ConsultantDeadlineActionsService implements IConsultantDeadlineActionsService {
  constructor(@inject(HttpService) private readonly http: IHttpService) {}

  createBulk(dto: ConsultantBulkCreateDeadlinesDto): Promise<ConsultantBulkCreateReport> {
    return this.http.post<ConsultantBulkCreateReport>('consultant/deadlines/bulk', dto);
  }

  previewDocument(
    tenantId: string,
    file: File,
    signal?: AbortSignal,
  ): Promise<DeadlineAiPreviewDto> {
    const body = new FormData();
    body.append('file', file);
    body.append('tenantId', tenantId);
    return this.http.postForm<DeadlineAiPreviewDto>('consultant/deadlines/ai-preview', body, {
      timeout: DEADLINE_AI_PREVIEW_TIMEOUT_MS,
      signal,
    });
  }

  stageDocument(tenantId: string, file: File, signal?: AbortSignal): Promise<DeadlineAiPreviewDto> {
    const body = new FormData();
    body.append('file', file);
    body.append('tenantId', tenantId);
    return this.http.postForm<DeadlineAiPreviewDto>('consultant/deadlines/upload-staging', body, {
      timeout: DEADLINE_AI_PREVIEW_TIMEOUT_MS,
      signal,
    });
  }

  getAiQuota(tenantId: string): Promise<DeadlineAiQuotaDto> {
    const query = new URLSearchParams({ tenantId }).toString();
    return this.http.get<DeadlineAiQuotaDto>(`consultant/deadlines/ai-preview/quota?${query}`);
  }

  update(deadlineId: string, dto: ConsultantUpdateDeadlineDto): Promise<DeadlineSummaryDto> {
    return this.http.patch<DeadlineSummaryDto>(`consultant/deadlines/${deadlineId}`, dto);
  }

  archive(deadlineId: string, reason?: DeadlineArchiveReason): Promise<DeadlineSummaryDto> {
    return this.http.post<DeadlineSummaryDto>(
      `consultant/deadlines/${deadlineId}/archive`,
      reason ? { reason } : {},
    );
  }

  restore(deadlineId: string): Promise<DeadlineSummaryDto> {
    return this.http.post<DeadlineSummaryDto>(`consultant/deadlines/${deadlineId}/restore`);
  }

  async remove(deadlineId: string, reason?: string): Promise<void> {
    const query = reason ? `?${new URLSearchParams({ reason }).toString()}` : '';
    await this.http.delete(`consultant/deadlines/${deadlineId}${query}`);
  }

  renew(deadlineId: string, input: RenewConsultantDeadlineInput): Promise<DeadlineSummaryDto> {
    const body = new FormData();
    if (input.file) {
      body.append('file', input.file);
    }
    if (input.uploadToken) {
      body.append('uploadToken', input.uploadToken);
    }
    const fields: Array<[string, string | undefined]> = [
      ['issuedAt', input.issuedAt],
      ['dueAt', input.dueAt],
      [
        'validityMonths',
        input.validityMonths === undefined ? undefined : String(input.validityMonths),
      ],
      [
        'requestClientSignature',
        input.requestClientSignature === undefined
          ? undefined
          : String(input.requestClientSignature),
      ],
    ];
    for (const [name, value] of fields) {
      if (value !== undefined) {
        body.append(name, value);
      }
    }
    if (input.assigneeUserId !== undefined) {
      body.append('assigneeUserId', input.assigneeUserId ?? '');
    }
    return this.http.postForm<DeadlineSummaryDto>(`consultant/deadlines/${deadlineId}/renew`, body);
  }

  takeOver(deadlineId: string, input: ConsultantTakeOverInput): Promise<DeadlineSummaryDto> {
    return this.http.post<DeadlineSummaryDto>(
      `consultant/deadlines/${deadlineId}/take-over`,
      input,
    );
  }

  cancelTakeOver(deadlineId: string): Promise<DeadlineSummaryDto> {
    return this.http.post<DeadlineSummaryDto>(
      `consultant/deadlines/${deadlineId}/take-over/cancel`,
    );
  }

  claim(dto: ConsultantClaimDeadlinesDto): Promise<ConsultantClaimReport> {
    return this.http.post<ConsultantClaimReport>('consultant/deadlines/claim', dto);
  }

  async getDocumentUrl(deadlineId: string): Promise<string> {
    const { url } = await this.http.get<{ url: string }>(
      `consultant/deadlines/${deadlineId}/document-url`,
    );
    return url;
  }

  getClaimable(): Promise<ConsultantClaimableResponse> {
    return this.http.get<ConsultantClaimableResponse>('consultant/deadlines/claimable');
  }
}

export { ConsultantDeadlineActionsService };
